import { cn } from '@/lib/cn';
import type { Job } from '@uchat-team-management/shared';
import { For, Show, createMemo } from 'solid-js';
import { dateTime, duration, secondsBetween, timeOnly, useNow } from './format';

const barColor = (j: Job) =>
  j.status !== 'completed'
    ? 'bg-brand-500 animate-pulse motion-reduce:animate-none'
    : j.conclusion === 'success'
      ? 'bg-emerald-500'
      : j.conclusion === 'cancelled' || j.conclusion === 'skipped'
        ? 'bg-neutral-400'
        : 'bg-red-500';

/** One lane per runner; each bar spans a job's start → end within [from, to]. */
export const JobTimeline = (props: { jobs: Job[]; from: number; to: number }) => {
  const now = useNow(5000);
  const lanes = createMemo(() => {
    const byRunner = new Map<string, Job[]>();
    for (const j of props.jobs) {
      if (!j.startedAt || !j.runnerName) continue;
      const key = `${j.runnerName} · ${j.repo.split('/')[1] ?? j.repo}`;
      byRunner.set(key, [...(byRunner.get(key) ?? []), j]);
    }
    return [...byRunner.entries()].sort(([a], [b]) => a.localeCompare(b));
  });
  const span = () => Math.max(1, props.to - props.from);
  const left = (ms: number) => `${Math.max(0, Math.min(100, ((ms - props.from) / span()) * 100))}%`;
  const ticks = () => [0, 0.25, 0.5, 0.75, 1].map((f) => props.from + f * span());

  return (
    <div class="rounded-xl border border-neutral-200 bg-surface p-4 dark:border-neutral-800 dark:bg-surface-dark">
      <Show
        when={lanes().length > 0}
        fallback={
          <p class="py-6 text-center text-sm text-neutral-500">No jobs ran in this window.</p>
        }
      >
        <div class="space-y-2">
          <For each={lanes()}>
            {([runner, jobs]) => (
              <div class="flex items-center gap-3">
                <div class="w-44 shrink-0 truncate text-xs font-medium" title={runner}>
                  {runner}
                </div>
                <div class="relative h-6 flex-1 rounded bg-neutral-100 dark:bg-neutral-800">
                  <For each={jobs}>
                    {(j) => {
                      const start = () => Date.parse(j.startedAt as string);
                      const end = () => (j.completedAt ? Date.parse(j.completedAt) : now());
                      const width = () =>
                        `${Math.max(0.4, ((Math.min(end(), props.to) - Math.max(start(), props.from)) / span()) * 100)}%`;
                      return (
                        <a
                          href={j.htmlUrl}
                          target="_blank"
                          rel="noreferrer"
                          class={cn(
                            'absolute top-0.5 bottom-0.5 rounded-sm transition-opacity duration-200 hover:opacity-80 motion-reduce:transition-none',
                            barColor(j),
                          )}
                          style={`left:${left(Math.max(start(), props.from))};width:${width()}`}
                          title={`${j.workflowName} / ${j.jobName}\n${dateTime(j.startedAt)} → ${j.completedAt ? dateTime(j.completedAt) : 'running'}\n${duration(secondsBetween(j.startedAt, j.completedAt, now()))} · ${j.conclusion ?? 'running'}`}
                        >
                          <span class="sr-only">
                            {j.jobName} {j.conclusion ?? 'running'}
                          </span>
                        </a>
                      );
                    }}
                  </For>
                </div>
              </div>
            )}
          </For>
          <div class="flex items-center gap-3">
            <div class="w-44 shrink-0" />
            <div class="relative h-4 flex-1 text-[10px] text-neutral-500">
              <For each={ticks()}>
                {(t, i) => (
                  <span
                    class="absolute -translate-x-1/2 tabular-nums"
                    classList={{ 'translate-x-0': i() === 0, '-translate-x-full': i() === 4 }}
                    style={`left:${left(t)}`}
                  >
                    {timeOnly(new Date(t).toISOString())}
                  </span>
                )}
              </For>
            </div>
          </div>
        </div>
      </Show>
    </div>
  );
};
